import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export interface IndividualDiffControlsOptions {
  onAcceptChange: (pos: number, mark: any) => void;
  onRejectChange: (pos: number, mark: any) => void;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    individualDiffControls: {
      acceptIndividualChange: (pos: number, mark: any) => ReturnType;
      rejectIndividualChange: (pos: number, mark: any) => ReturnType;
      acceptConsecutiveDiffs: (from: number, to: number, marks: any[]) => ReturnType;
      rejectConsecutiveDiffs: (from: number, to: number, marks: any[]) => ReturnType;
    };
  }
}

interface DiffGroup {
  from: number;
  to: number;
  marks: Array<{ mark: any; from: number; to: number }>;
  type: 'insertion' | 'deletion' | 'mixed';
}

export const IndividualDiffControls = Extension.create<IndividualDiffControlsOptions>({
  name: 'individualDiffControls',

  addOptions() {
    return {
      onAcceptChange: () => {},
      onRejectChange: () => {},
    };
  },

  addCommands() {
    const findMarkRange = ($pos: any, targetMark: any) => {
      const parent = $pos.parent;
      let start = $pos.pos - $pos.parentOffset;

      // Find continuous range with same mark
      let foundRange = null;
      parent.forEach((node: any, offset: number) => {
        if (
          node.isText &&
          node.marks.some(
            (mark: any) =>
              mark.type === targetMark.type && JSON.stringify(mark.attrs) === JSON.stringify(targetMark.attrs),
          )
        ) {
          const nodeStart = start + offset;
          const nodeEnd = nodeStart + node.nodeSize;

          // Check if this is the target node
          if ($pos.pos >= nodeStart && $pos.pos <= nodeEnd) {
            foundRange = { from: nodeStart, to: nodeEnd };
          }
        }
      });

      // Fallback to simple range
      return foundRange || { from: $pos.pos, to: $pos.pos + 1 };
    };

    return {
      acceptIndividualChange:
        (pos: number, mark: any) =>
        ({ tr, state }) => {
          // Find the exact range of this mark
          const $pos = state.doc.resolve(pos);
          const markRange = findMarkRange($pos, mark);

          if (markRange) {
            if (mark.type.name === 'insertion') {
              // For insertions, just remove the mark (accept the content)
              tr.removeMark(markRange.from, markRange.to, mark.type);
            } else if (mark.type.name === 'deletion') {
              // For deletions, remove the content entirely (accept the deletion)
              // Check if this is a placeholder space for an empty paragraph
              const node = state.doc.nodeAt(markRange.from);
              if (node && node.isText && node.text === ' ' && node.nodeSize === 1) {
                // This is our placeholder space for empty paragraph - delete the entire draggableBlock
                let depth = $pos.depth;

                // Walk up to find draggableBlock
                while (depth > 0 && $pos.node(depth).type.name !== 'draggableBlock') {
                  depth--;
                }

                if (depth > 0 && $pos.node(depth).type.name === 'draggableBlock') {
                  const blockStart = $pos.before(depth);
                  const blockEnd = blockStart + $pos.node(depth).nodeSize;
                  tr.delete(blockStart, blockEnd);
                } else {
                  // Fallback: just delete the text
                  tr.delete(markRange.from, markRange.to);
                }
              } else {
                // Regular deletion - just delete the text
                tr.delete(markRange.from, markRange.to);
              }
            }
            return true;
          }
          return false;
        },

      rejectIndividualChange:
        (pos: number, mark: any) =>
        ({ tr, state }) => {
          // Find the exact range of this mark
          const $pos = state.doc.resolve(pos);
          const markRange = findMarkRange($pos, mark);

          if (markRange) {
            if (mark.type.name === 'insertion') {
              // For insertions, remove the content (reject the addition)
              tr.delete(markRange.from, markRange.to);
            } else if (mark.type.name === 'deletion') {
              // For deletions, just remove the mark (reject the deletion, keep content)
              tr.removeMark(markRange.from, markRange.to, mark.type);
            }
            return true;
          }
          return false;
        },

      acceptConsecutiveDiffs:
        (from: number, to: number, marks: any[]) =>
        ({ tr, state, commands }) => {
          // Process marks in reverse order to avoid position shifting
          const sortedMarks = [...marks].sort((a, b) => b.from - a.from);

          sortedMarks.forEach(({ mark, from: markFrom, to: markTo }) => {
            if (mark.type.name === 'insertion') {
              tr.removeMark(markFrom, markTo, mark.type);
            } else if (mark.type.name === 'deletion') {
              const node = state.doc.nodeAt(markFrom);
              if (node && node.isText && node.text === ' ' && node.nodeSize === 1) {
                const $pos = state.doc.resolve(markFrom);
                let depth = $pos.depth;
                while (depth > 0 && $pos.node(depth).type.name !== 'draggableBlock') {
                  depth--;
                }
                if (depth > 0 && $pos.node(depth).type.name === 'draggableBlock') {
                  const blockStart = $pos.before(depth);
                  const blockEnd = blockStart + $pos.node(depth).nodeSize;
                  tr.delete(blockStart, blockEnd);
                } else {
                  tr.delete(markFrom, markTo);
                }
              } else {
                tr.delete(markFrom, markTo);
              }
            }
          });

          return true;
        },

      rejectConsecutiveDiffs:
        (from: number, to: number, marks: any[]) =>
        ({ tr, state }) => {
          // Process marks in reverse order to avoid position shifting
          const sortedMarks = [...marks].sort((a, b) => b.from - a.from);

          sortedMarks.forEach(({ mark, from: markFrom, to: markTo }) => {
            if (mark.type.name === 'insertion') {
              tr.delete(markFrom, markTo);
            } else if (mark.type.name === 'deletion') {
              tr.removeMark(markFrom, markTo, mark.type);
            }
          });

          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    const extension = this;

    return [
      new Plugin({
        key: new PluginKey('individualDiffControls'),
        props: {
          decorations(state) {
            const decorations: Decoration[] = [];
            const diffGroups: DiffGroup[] = [];

            // First pass: collect all diff marks and group consecutive ones
            let currentGroup: DiffGroup | null = null;
            let lastPos = -1;

            state.doc.descendants((node, pos) => {
              if (node.isText) {
                const diffMarks = node.marks.filter(
                  (mark) => mark.type.name === 'insertion' || mark.type.name === 'deletion',
                );

                if (diffMarks.length > 0) {
                  const endPos = pos + node.nodeSize;

                  // Check if this is consecutive with the last diff
                  if (currentGroup && pos === lastPos) {
                    // Add to current group
                    diffMarks.forEach((mark) => {
                      currentGroup!.marks.push({ mark, from: pos, to: endPos });
                      currentGroup!.to = endPos;

                      // Update group type if mixed
                      if (currentGroup!.type !== 'mixed') {
                        if (currentGroup!.type !== mark.type.name) {
                          currentGroup!.type = 'mixed';
                        }
                      }
                    });
                  } else {
                    // Save previous group if exists
                    if (currentGroup) {
                      diffGroups.push(currentGroup);
                    }

                    // Start new group
                    currentGroup = {
                      from: pos,
                      to: endPos,
                      marks: diffMarks.map((mark) => ({ mark, from: pos, to: endPos })),
                      type: diffMarks.length === 1 ? (diffMarks[0].type.name as 'insertion' | 'deletion') : 'mixed',
                    };
                  }

                  lastPos = endPos;
                } else if (currentGroup) {
                  // No diff marks, save current group
                  diffGroups.push(currentGroup);
                  currentGroup = null;
                }
              } else if (currentGroup) {
                // Non-text node, save current group
                diffGroups.push(currentGroup);
                currentGroup = null;
              }
            });

            // Don't forget the last group
            if (currentGroup) {
              diffGroups.push(currentGroup);
            }

            // Second pass: create decorations for each group
            diffGroups.forEach((group, groupIndex) => {
              const decoration = Decoration.widget(
                group.to,
                () => {
                  const container = document.createElement('span');
                  container.className = 'diff-actions diff-actions-combined';

                  // Accept button
                  const acceptBtn = document.createElement('button');
                  acceptBtn.className = 'diff-btn accept';
                  acceptBtn.innerHTML = '✓';

                  // Update tooltip based on group type
                  if (group.type === 'insertion') {
                    acceptBtn.title = 'Accept Addition';
                  } else if (group.type === 'deletion') {
                    acceptBtn.title = 'Accept Deletion';
                  } else {
                    acceptBtn.title = 'Accept Changes';
                  }

                  acceptBtn.onclick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (group.marks.length === 1) {
                      // Single mark - use individual command
                      extension.editor.commands.acceptIndividualChange(group.marks[0].from, group.marks[0].mark);
                    } else {
                      // Multiple marks - use batch command
                      extension.editor.commands.acceptConsecutiveDiffs(group.from, group.to, group.marks);
                    }
                  };

                  // Reject button
                  const rejectBtn = document.createElement('button');
                  rejectBtn.className = 'diff-btn reject';
                  rejectBtn.innerHTML = '✕';

                  // Update tooltip based on group type
                  if (group.type === 'insertion') {
                    rejectBtn.title = 'Reject Addition';
                  } else if (group.type === 'deletion') {
                    rejectBtn.title = 'Reject Deletion';
                  } else {
                    rejectBtn.title = 'Reject Changes';
                  }

                  rejectBtn.onclick = (e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (group.marks.length === 1) {
                      // Single mark - use individual command
                      extension.editor.commands.rejectIndividualChange(group.marks[0].from, group.marks[0].mark);
                    } else {
                      // Multiple marks - use batch command
                      extension.editor.commands.rejectConsecutiveDiffs(group.from, group.to, group.marks);
                    }
                  };

                  container.appendChild(acceptBtn);
                  container.appendChild(rejectBtn);

                  return container;
                },
                {
                  side: 1,
                  key: `diff-group-${group.from}-${group.to}-${groupIndex}`,
                },
              );

              decorations.push(decoration);
            });

            return DecorationSet.create(state.doc, decorations);
          },
        },
      }),
    ];
  },
});

export default IndividualDiffControls;
